import { fontManager } from '../../../shared/lib/fontManager';
import { resolveTextLayout, resolveBandLayout, colorWithOpacity } from '../../../shared/lib';
import { fitTextBlock, autoFitFontSize } from '../../../shared/lib/text';
import { fillTextSplit } from '../../../shared/lib/text/fillTextSplit';
import { DEFAULT_PREVIEW_FONT_SIZE } from '../../../shared/lib/video';
import { exportTemplateToBlob as exportToBlobUtil } from '../../../shared/lib/canvas-export';
import { applyFontVariationToCanvas } from '../../../shared/lib/variableFontUtils';
import { computeTopCardRects, computeFrameTextBox, clampFrameTextY, centerBlockInBand, frameTextAvailableHeight } from '../../../shared/lib/postLayout';
import { measureFrameTextSpace, measureBandFill } from '../../../shared/lib/frameBand';
import { clampInstagramAspect } from '../../../shared/lib/instagramAspect';
import { ensureCanvasFont } from '../../../shared/lib/fontLoader';

/** Отступ блока заголовка от края кадра в макете frame, render-space px @1350. */
const FRAME_TEXT_MARGIN = 24;

export type MediaRect = { x: number; y: number; w: number; h: number };

/**
 * Прямоугольник отрисовки медиа внутри rect — 1:1 с CSS object-fit превью без шаблона:
 * fill = object-cover (кроп по центру), fit = object-contain (по центру).
 */
export function mediaDrawRect(
  imgAspect: number,
  rect: MediaRect,
  fitMode: 'fill' | 'fit',
): MediaRect {
  const targetAspect = rect.w / rect.h;
  const wider = imgAspect > targetAspect;
  const coverSide = fitMode === 'fill' ? wider : !wider;
  // coverSide=true → упираемся в высоту rect, иначе в ширину.
  const h = coverSide ? rect.h : rect.w / imgAspect;
  const w = coverSide ? rect.h * imgAspect : rect.w;
  return { x: rect.x + (rect.w - w) / 2, y: rect.y + (rect.h - h) / 2, w, h };
}

/**
 * Рисует медиа в rect с учётом fitMode. Единая точка для всех макетов
 * (bottom-bar / top-card / frame). В режиме fit поля закрываются размытой
 * копией фото (cover ×1.1) — как в превью, пустых краёв не остаётся.
 */
function drawMediaFitted(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  rect: MediaRect,
  fitMode: 'fill' | 'fit',
): void {
  const imgAspect = img.width / img.height;

  if (fitMode === 'fit') {
    const cover = mediaDrawRect(imgAspect, rect, 'fill');
    const scaleFactor = 1.1;
    const sw = cover.w * scaleFactor, sh = cover.h * scaleFactor;
    const sx = (rect.w - sw) / 2, sy = (rect.h - sh) / 2;
    // Блюр через downscale→upscale: ctx.filter поддержан не везде (iOS WKWebView).
    const blurScale = 40;
    const blurCanvas = document.createElement('canvas');
    blurCanvas.width = Math.max(1, Math.floor(rect.w / blurScale));
    blurCanvas.height = Math.max(1, Math.floor(rect.h / blurScale));
    const blurCtx = blurCanvas.getContext('2d');
    if (blurCtx) {
      blurCtx.imageSmoothingEnabled = true;
      blurCtx.imageSmoothingQuality = 'high';
      blurCtx.drawImage(img, sx / blurScale, sy / blurScale, sw / blurScale, sh / blurScale);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(blurCanvas, rect.x, rect.y, rect.w, rect.h);
    }
  }

  const d = mediaDrawRect(imgAspect, rect, fitMode);
  ctx.drawImage(img, d.x, d.y, d.w, d.h);
}

export interface ExportOptions {
  fileName?: string;
  backgroundColor: string;
  useGradient?: boolean;
  backgroundColorEnd?: string;
  backgroundAlpha?: number;
  backgroundAlphaEnd?: number;
  textColor: string;
  useTwoColors?: boolean;
  textColorSecondary?: string;
  fontFamily: string;
  fontUrl?: string; // Добавляем fontUrl для гарантированной загрузки
  fontName?: string; // Добавляем fontName
  fontSize?: number; // Размер шрифта из шаблона
  logoUrl?: string;
  logoPosition?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-right';
  logoSize?: number; // square logo box side (px). Default 240.
  textLineSpacing?: number; // text line-height ratio. Default 1.2.
  imageUrl?: string;
  text?: string;
  textUppercase?: boolean;
  fontBold?: boolean; // Жирный шрифт
  fontVariationSettings?: Record<string, number>; // Variable font
  fitMode?: 'fill' | 'fit'; // Режим отображения медиа
  /**
   * Высота холста по аспекту фото (клампд под фид IG), а не фикс. 4:5 —
   * чтобы пост с шаблоном кадрировался так же, как превью без шаблона.
   * Не действует на макет frame: каркас-PNG нарисован под 1080×1350.
   */
  fitCanvasToMedia?: boolean;
  showLine?: boolean; // Показывать ли декоративную линию
  lineColor?: string; // Цвет линии
  lineHeight?: number; // Высота линии в px
  linePosition?: 'top' | 'bottom'; // Позиция линии
  /**
   * Реальная ширина PostTemplate в DOM (для правильного масштабирования)
   */
  sourceWidth?: number;
  textShadowEnabled?: boolean;
  textShadowColor?: string;
  textShadowBlur?: number;
  textShadowWidth?: number;
  textSoftShadowEnabled?: boolean;
  textSoftShadowColor?: string;
  textSoftShadowBlur?: number;
  textSoftShadowOffsetX?: number;
  textSoftShadowOffsetY?: number;
  textSoftShadowOpacity?: number;
  textShadowOpacity?: number;
  // Макет поста
  postLayout?: 'bottom-bar' | 'top-card' | 'frame';
  accentColor?: string;
  cardColor?: string;
  cardInset?: number;
  bandHeight?: number;
  frameUrl?: string;
  frameTextX?: number;
  frameTextY?: number;
  frameTextWidth?: number;
  frameTextVAlign?: 'top' | 'center' | 'bottom';
  /** Потолок авто-кегля, render px @1080. Не задан — AUTO_FONT_RANGE.max. */
  autoFontMax?: number;
  /** frame: дорисовывать плашку каркаса вверх под текст (цвет сэмплится из PNG). */
  bandGrow?: boolean;
  textAlign?: 'left' | 'center' | 'right';
  /**
   * Рендер сообщает, каким кеглем он в итоге нарисовал заголовок и какой кегль
   * ещё влезает в плашку — оба в preview-space px. Нужно интерфейсу: показать
   * авто-размер и не дать кнопке A+ выйти за потолок. Считать это в форме
   * нельзя — там нет ни канваса, ни загруженного шрифта.
   */
  onResolvedFontSize?: (info: { fontSize: number; max: number }) => void;
}

/**
 * Конвертирует HEX в RGBA
 */
function hexToRgba(hex: string, alpha: number): string {
  // Убираем # если есть
  hex = hex.replace('#', '');
  
  // Конвертируем в RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Загружает изображение
 */
// Кеш декодированных картинок по src. Без него каждый рендер (напр. шаг размера
// шрифта на превью) заново декодит фото/лого/каркас → лаг. Декодим один раз.
const imageCache = new Map<string, HTMLImageElement>();

function loadImage(src: string): Promise<HTMLImageElement> {
  const cached = imageCache.get(src);
  if (cached?.complete && cached.naturalWidth > 0) return Promise.resolve(cached);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { imageCache.set(src, img); resolve(img); };
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * Экспортирует шаблон поста в PNG через Canvas API
 */
export async function exportTemplateToPNG(options: ExportOptions): Promise<void> {
  const blob = await exportTemplateToBlob(options);
  
  const { fileName = `instagram-post-${Date.now()}.png` } = options;
  
  // Скачиваем файл
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Экспортирует шаблон в Blob (для preview и скачивания)
 * ОПТИМИЗИРОВАНО: параллельная загрузка, минимум await
 */
export async function exportTemplateToBlob(options: ExportOptions): Promise<Blob> {
  const {
    backgroundColor,
    useGradient,
    backgroundColorEnd,
    backgroundAlpha,
    backgroundAlphaEnd,
    textColor,
    useTwoColors,
    textColorSecondary,
    fontFamily,
    fontUrl,
    fontName,
    fontSize,
    logoUrl,
    logoPosition = 'top-left',
    imageUrl,
    text = 'Пример текста поста',
    textUppercase = false,
    fontBold = false, // Жирный шрифт
    fontVariationSettings, // Variable font
    fitMode = 'fill',
    showLine = false,
    lineColor = '#000000',
    lineHeight = 2,
    linePosition = 'top',
    textShadowEnabled = false,
    textShadowColor = '#000000',
    textShadowBlur = 4,
    textShadowWidth = 4,
    textSoftShadowEnabled = false,
    textSoftShadowColor = '#000000',
    textSoftShadowBlur = 8,
    textSoftShadowOffsetX = 2,
    textSoftShadowOffsetY = 4,
    textSoftShadowOpacity = 100,
    textShadowOpacity = 100,
    sourceWidth,
    postLayout = 'bottom-bar',
    accentColor = '#FFD700',
    cardColor = '#FFFFFF',
    cardInset = 32,
    bandHeight = 0.22,
    frameUrl,
    frameTextX = 0.5,
    frameTextY = 0.12,
    frameTextWidth = 0.8,
    frameTextVAlign = 'top',
    textAlign = 'center',
    fitCanvasToMedia = false,
  } = options;
  // Хелпер: X и canvas-align для строки внутри центра cx с шириной maxW.
  const alignedX = (cx: number, maxW: number): { x: number; a: CanvasTextAlign } => {
    if (textAlign === 'left') return { x: cx - maxW / 2, a: 'left' };
    if (textAlign === 'right') return { x: cx + maxW / 2, a: 'right' };
    return { x: cx, a: 'center' };
  };

  try {
    // ⚡ ОПТИМИЗАЦИЯ 1: Параллельная загрузка шрифта и изображений
    const loadPromises: Promise<any>[] = [];
    
    // Загружаем шрифт
    let actualFontFamily = fontFamily;
    if (fontUrl && fontName) {
      loadPromises.push(
        fontManager.ensureFontLoaded(fontUrl, fontName).then(family => {
          actualFontFamily = family;
        })
      );
    }
    
    // Загружаем изображение
    let imgPromise: Promise<HTMLImageElement | null> = Promise.resolve(null);
    if (imageUrl) {
      imgPromise = loadImage(imageUrl);
      loadPromises.push(imgPromise);
    }
    
    // Загружаем лого
    let logoPromise: Promise<HTMLImageElement | null> = Promise.resolve(null);
    if (logoUrl) {
      logoPromise = loadImage(logoUrl);
      loadPromises.push(logoPromise);
    }

    // Загружаем каркас (frame)
    let framePromise: Promise<HTMLImageElement | null> = Promise.resolve(null);
    if (frameUrl) {
      framePromise = loadImage(frameUrl);
      loadPromises.push(framePromise);
    }
    
    // Ждем все ресурсы ПАРАЛЛЕЛЬНО
    await Promise.all(loadPromises);
    
    // ⚡ ОПТИМИЗАЦИЯ 2: Убираем лишний setTimeout
    await document.fonts.ready;
    // iOS WKWebView (Telegram): прогрев шрифта, иначе canvas рисует запасным.
    const fontOk = await ensureCanvasFont(actualFontFamily, fontUrl);
    // Кастомный шрифт обязан загрузиться: тихий фолбэк уже уезжал в
    // опубликованные посты тонким системным начертанием (shymda_, 04.09).
    // Лучше остановить публикацию с понятной ошибкой, чем выпустить не тот вид.
    if (!fontOk && fontUrl) {
      throw new Error('Шрифт шаблона не загрузился — проверьте интернет и попробуйте ещё раз');
    }
    
    // Вычисляем масштаб на основе реальной ширины PostTemplate
    // Если не передано, используем типичное значение 400px (max-w-md)
    const realSourceWidth = sourceWidth || 400;
    const scale = 1080 / realSourceWidth;
    
    console.log('🎨 Export scale:', { realSourceWidth, scale });

    // Высота холста: фикс. 4:5 либо аспект фото, клампд под фид IG (3:4…1.91:1) —
    // тогда кадрирование совпадает с превью без шаблона. frame всегда 1350: каркас фикс.
    const mediaImg = await imgPromise;
    const canvasHeight = fitCanvasToMedia && mediaImg && postLayout !== 'frame'
      ? Math.round(1080 / clampInstagramAspect(mediaImg.width / mediaImg.height))
      : 1350;

    // Создаём canvas с размерами поста Instagram
    const canvas = document.createElement('canvas');
    canvas.width = 1080;
    canvas.height = canvasHeight;

    const ctx = canvas.getContext('2d', {
      alpha: true,
      colorSpace: 'srgb',
    });

    if (!ctx) {
      throw new Error('Could not get canvas context');
    }

    // 1. Заливаем белый фон (кроме frame — там прозрачные зоны каркаса должны
    //    оставаться прозрачными, чтобы показывать фон-подложку превью).
    if (postLayout !== 'frame') {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 1080, canvasHeight);
    }

    // ── Макет top-card: плашка с текстом сверху, фото full-bleed снизу ──
    if (postLayout === 'top-card') {
      const rects = computeTopCardRects({ width: 1080, height: canvasHeight, bandHeight, cardInset });

      // Фото в нижнюю область (cover внутри photo-прямоугольника).
      const photoImg = mediaImg;
      if (photoImg) {
        const pr = rects.photo;
        ctx.save();
        ctx.beginPath();
        ctx.rect(pr.x, pr.y, pr.w, pr.h);
        ctx.clip();
        drawMediaFitted(ctx, photoImg, pr, fitMode);
        ctx.restore();
      }

      // Акцентная подложка + внутренняя карточка.
      ctx.fillStyle = accentColor;
      ctx.fillRect(rects.accent.x, rects.accent.y, rects.accent.w, rects.accent.h);
      ctx.fillStyle = cardColor;
      ctx.fillRect(rects.card.x, rects.card.y, rects.card.w, rects.card.h);

      // Логотип.
      const logoImgTc = await logoPromise;
      const logoBoxTc = options.logoSize && options.logoSize > 0 ? options.logoSize : 96 * scale;
      const padTc = 16 * scale;
      if (logoImgTc && logoPosition) {
        let lx = padTc, ly = padTc;
        if (logoPosition === 'bottom-left') { lx = padTc; ly = canvasHeight - logoBoxTc - padTc; }
        else if (logoPosition === 'bottom-center') { lx = 540 - logoBoxTc / 2; ly = canvasHeight - logoBoxTc - padTc; }
        else if (logoPosition === 'bottom-right') { lx = 1080 - logoBoxTc - padTc; ly = canvasHeight - logoBoxTc - padTc; }
        else if (logoPosition === 'top-right') { lx = 1080 - logoBoxTc - padTc; ly = padTc; }
        else if (logoPosition === 'top-center') { lx = 540 - logoBoxTc / 2; ly = padTc; }
        else { lx = padTc; ly = padTc; } // top-left
        ctx.drawImage(logoImgTc, lx, ly, logoBoxTc, logoBoxTc);
      }

      // Текст по центру карточки.
      if (text && text.trim()) {
        const processedText = textUppercase ? text.toUpperCase() : text;
        const maxWidth = rects.card.w * 0.9;
        const maxHeight = rects.card.h * 0.8;
        // Точный размер (per-account) → 1:1 с превью; авто-фит только без размера.
        const { fontSize: optimalFontSize, lines } = resolveTextLayout({
          text: processedText, fontSize, scale, maxWidth, maxHeight,
          fontFamily: actualFontFamily, ctx,
        });

        if (textShadowEnabled) {
          ctx.strokeStyle = colorWithOpacity(textShadowColor, textShadowOpacity ?? 100);
          ctx.lineWidth = (textShadowWidth ?? 4) * 2 * scale;
          ctx.lineJoin = 'round';
          ctx.shadowColor = colorWithOpacity(textShadowColor, textShadowOpacity ?? 100);
          ctx.shadowBlur = textShadowBlur * scale;
        }
        applyFontVariationToCanvas(ctx, optimalFontSize, actualFontFamily, fontVariationSettings, fontBold);
        ctx.fillStyle = textColor;
        ctx.textBaseline = 'middle';

        const textCx = rects.card.x + rects.card.w / 2;
        const { x: textX, a: drawAlign } = alignedX(textCx, rects.card.w * 0.9);
        ctx.textAlign = drawAlign;
        const textY = rects.card.y + rects.card.h / 2;
        const lh = optimalFontSize * (options.textLineSpacing && options.textLineSpacing > 0 ? options.textLineSpacing : 1.2);
        const startY = textY - (lines.length * lh) / 2 + lh / 2;

        if (textSoftShadowEnabled) {
          ctx.save();
          ctx.shadowColor = colorWithOpacity(textSoftShadowColor, textSoftShadowOpacity ?? 100);
          ctx.shadowBlur = textSoftShadowBlur * scale;
          ctx.shadowOffsetX = (textSoftShadowOffsetX ?? 2) * scale;
          ctx.shadowOffsetY = (textSoftShadowOffsetY ?? 4) * scale;
          // Большое размытие бледнит тень → несколько проходов аккумулируют альфу.
          const softPasses = Math.min(3, 1 + Math.round(textSoftShadowBlur / 14));
          for (let p = 0; p < softPasses; p++) lines.forEach((line, i) => {
            if (!line.trim()) return;
            ctx.fillStyle = useTwoColors && textColorSecondary && i !== 0 ? textColorSecondary : textColor;
            ctx.fillText(line, textX, startY + i * lh);
          });
          ctx.restore();
        }

        lines.forEach((line, i) => {
          if (useTwoColors && textColorSecondary) ctx.fillStyle = i === 0 ? textColor : textColorSecondary;
          if (line.trim()) {
            if (textShadowEnabled) ctx.strokeText(line, textX, startY + i * lh);
            ctx.fillText(line, textX, startY + i * lh);
          }
        });
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      }

      return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (!blob) { reject(new Error('Failed to create blob from canvas')); return; }
          resolve(blob);
        }, 'image/png', 1.0);
      });
    }

    // ── Макет frame: фото cover → каркас-PNG поверх → текст → лого ──
    if (postLayout === 'frame') {
      // 1. Фото на весь канвас: cover (fill) либо размытая подложка + contain (fit).
      const photoImg = await imgPromise;
      if (photoImg) {
        drawMediaFitted(ctx, photoImg, { x: 0, y: 0, w: 1080, h: 1350 }, fitMode);
      }

      // 2. Каркас-PNG на весь канвас.
      const frameImg = await framePromise;
      if (frameImg) ctx.drawImage(frameImg, 0, 0, 1080, 1350);

      // 3. Текст по координатам слайдеров.
      if (text && text.trim()) {
        const box = computeFrameTextBox({ width: 1080, height: 1350, frameTextX, frameTextY, frameTextWidth });
        const processedText = textUppercase ? text.toUpperCase() : text;
        // Плашка тут — часть PNG-каркаса, растить её нельзя. Поэтому целимся в её
        // высоту, оценённую по якорю, и ужимаем кегль, но не ниже пола. Не влезло
        // даже на полу — блок всё равно не режем, его поднимет clampFrameTextY.
        // Размер задан в preview-пространстве → переводим в render-space (×scale).
        // Плашку меряем прямо в каркасе — ту, внутри которой стоит якорь.
        // Оценка по якорю ошибается: завышение до +38 px у 13 каркасов из 41,
        // а у ortalyq.news (текст сверху, заливка снизу) — на 1027.
        const halfW = box.maxWidth / 2 / 1080;
        const space = frameImg
          ? measureFrameTextSpace(frameImg, frameUrl ?? '', 1350, box.cy, box.cx / 1080 - halfW, box.cx / 1080 + halfW)
          : { band: null, contentTop: null };
        const band = space.band;
        // Ниже этой строки в каркасе нарисован логотип — туда заголовку нельзя.
        const contentTop = space.contentTop;
        // fitH — куда целится авто-подбор, limitH — предел для ручного размера.
        // Они разные нарочно: авто оставляет поля внутри плашки (иначе текст
        // встаёт впритык к её краям), а ручной может дожать до самого края —
        // и заодно у кнопки A+ появляется запас хода над авто-размером.
        let fitH: number;
        let limitH: number;
        let bandTopLimit: number | undefined;
        let bandBottomLimit: number | undefined;
        // Края измеренной плашки — по ним центрируется блок строк.
        let bandTopPx: number | undefined;
        let bandBottomPx: number | undefined;
        // Внутренние поля плашки — нужны и ниже, при дорисовке плашки вверх.
        let bandPad = 0;
        // На сколько px плашке разрешено вырасти вверх (bandGrow). 0 — нельзя.
        let bandGrowCap = 0;
        if (band) {
          // Плашка кончается либо своим низом, либо логотипом внутри неё.
          const bottom = contentTop !== null ? Math.min(band.bottom + 1, contentTop) : band.bottom + 1;
          const bandH = Math.max(FRAME_TEXT_MARGIN * 2, bottom - band.top);
          // Медиа (kaztwitter): «когда текст увеличивается, синий фон должен
          // становиться выше». С bandGrow целимся в плашку до полутора высот:
          // кегль держится крупным, а недостающую высоту дорисуем её же цветом.
          if (options.bandGrow) bandGrowCap = Math.round(bandH * 0.5);
          // Поля внутри плашки: без них текст встаёт впритык к её краям —
          // на steppe.daily упирался в оранжевую полосу и сверху, и снизу.
          //
          // Было 12% и минимум 28 px, и это оказалось слишком щедро: медиа
          // попросила «увеличить, где можно». Замер по всем 49 каркасам на трёх
          // длинах заголовка: 19 сочетаний выросли, до +27% (kaztwitter), новых
          // вылезаний за плашку не появилось. Ниже 6% не опускаю — тогда текст
          // начинает касаться краёв.
          const pad = Math.max(14, Math.round(bandH * 0.06));
          bandPad = pad;
          fitH = Math.max(FRAME_TEXT_MARGIN * 2, bandH + bandGrowCap - pad * 2);
          // Предел для ручного размера — почти вся плашка. Он заведомо больше
          // fitH, поэтому у кнопки A+ есть запас хода над авто-размером.
          limitH = Math.max(FRAME_TEXT_MARGIN * 2, bandH + bandGrowCap - 24);
          bandTopLimit = band.top + 12;
          bandBottomLimit = bottom - 12;
          bandTopPx = band.top;
          bandBottomPx = bottom;
        } else {
          // Плашки нет. Но если ниже якоря в каркасе нарисован логотип, дно
          // кадра — не граница: считаем место до логотипа. Это случай
          // atyrau.today06, где заголовок доезжал до лошадей.
          const floorY = contentTop ?? 1350;
          fitH = limitH = frameTextAvailableHeight({
            cy: box.cy,
            canvasHeight: floorY,
            vAlign: frameTextVAlign ?? 'top',
            margin: FRAME_TEXT_MARGIN,
          });
          if (contentTop !== null) bandBottomLimit = contentTop - 12;
        }
        const frameFit = {
          text: processedText,
          lineSpacing: options.textLineSpacing && options.textLineSpacing > 0 ? options.textLineSpacing : 1.2,
          maxWidth: box.maxWidth,
          maxHeight: limitH,
          fontFamily: actualFontFamily,
          ctx,
        };
        // Размер не задан → подбираем сами. Максимум, который влезает в плашку,
        // зажатый в коридор: дежурному не нужно знать ни шрифт, ни кегль, а
        // подходящее число он всё равно угадать не может — оно зависит не от
        // паблика, а от паблика × конкретного заголовка.
        // Задан вручную → рисуем ровно им. Не ужимаем: форма показывает это же
        // число и предупреждает «не влезает — выйдет за плашку», а тихая усадка
        // означала бы, что подпись, предупреждение и картинка говорят разное.
        const auto = autoFitFontSize({
          ...frameFit,
          maxHeight: fitH,
          ceilingMaxHeight: limitH,
          // По-шаблонный потолок: медиа просит у части пабликов «чуть меньше»,
          // при этом общий коридор остальным не трогаем.
          maxFontSize: options.autoFontMax,
        });
        const { fontSize: optimalFontSize, lines, primaryLines, splitChars } =
          fontSize && fontSize > 0
            ? fitTextBlock({ ...frameFit, fontSize: fontSize * scale, minFontScale: 1 })
            : auto;
        // Наверх отдаём в preview-space (÷ scale) — в этих единицах живут A−/A+.
        options.onResolvedFontSize?.({
          fontSize: optimalFontSize / scale,
          max: auto.ceiling / scale,
        });
        if (textShadowEnabled) {
          ctx.strokeStyle = colorWithOpacity(textShadowColor, textShadowOpacity ?? 100);
          ctx.lineWidth = (textShadowWidth ?? 4) * 2 * scale;
          ctx.lineJoin = 'round';
          ctx.shadowColor = colorWithOpacity(textShadowColor, textShadowOpacity ?? 100);
          ctx.shadowBlur = textShadowBlur * scale;
        }
        applyFontVariationToCanvas(ctx, optimalFontSize, actualFontFamily, fontVariationSettings, fontBold);
        ctx.fillStyle = textColor;
        // Топ-якорь: первая строка стоит на Y, остальные добавляются вниз.
        ctx.textBaseline = 'top';
        const { x: fX, a: fAlign } = alignedX(box.cx, box.maxWidth);
        // Граница первого цвета может пройти ПОСРЕДИ строки (до двоеточия),
        // поэтому строку рисуем не одним fillText, а двумя кусками.
        const paintLine = (line: string, i: number, y: number) => {
          if (!useTwoColors || !textColorSecondary) {
            ctx.fillStyle = textColor;
            ctx.fillText(line, fX, y);
            return;
          }
          const chars = i < primaryLines ? line.length : i === primaryLines ? splitChars : 0;
          fillTextSplit(ctx, line, fX, y, chars, textColor, textColorSecondary);
        };
        ctx.textAlign = fAlign;
        const lh = optimalFontSize * (options.textLineSpacing && options.textLineSpacing > 0 ? options.textLineSpacing : 1.2);
        const blockH = lines.length * lh;
        const anchoredY = frameTextVAlign === 'center' ? box.cy - blockH / 2
          : frameTextVAlign === 'bottom' ? box.cy - blockH
          : box.cy;
        // Якорь стоит у самого низа кадра (у большинства шаблонов frameTextY ≈ 0.9),
        // поэтому длинный заголовок при центровке наполовину уезжает за нижний край.
        // Не режем и не мельчим — поднимаем блок ровно настолько, чтобы он влез.
        // Плашка измерена — ставим блок по её центру, а не по якорю шаблона.
        // Якорь у большинства каркасов стоит не в середине плашки, а когда блок
        // не помещался, clampFrameTextY выталкивал его целиком ВЫШЕ плашки:
        // заголовок уезжал на фотографию, где его почти не видно, и при этом
        // сама плашка снизу оставалась пустой. Теперь не помещается — вылезает
        // поровну вверх и вниз, оставаясь на плашке.
        // Метрики нужны, чтобы центрировать буквы, а не рамку строк. Шрифт в
        // ctx уже выставлен выше — меряем им же, чем и рисуем.
        const mFirst = ctx.measureText(lines[0] ?? '');
        const mLast = ctx.measureText(lines[lines.length - 1] ?? '');
        // actualBoundingBox* отсчитываются от ТЕКУЩЕГО textBaseline, а он 'top'.
        // Значит ascent здесь отрицательный: буквы начинаются ниже линии y.
        const inkTop = Math.max(0, -mFirst.actualBoundingBoxAscent);
        const inkBottom = (lines.length - 1) * lh + mLast.actualBoundingBoxDescent;
        // bandGrow: блок не влез в измеренную плашку — дорисовываем её вверх
        // её же цветом, ровно на недостающую высоту (в пределах bandGrowCap).
        // Цвет сэмплится из PNG; неоднотонная плашка — растить нечем, пропускаем.
        if (bandGrowCap > 0 && frameImg && bandTopPx !== undefined && bandBottomPx !== undefined) {
          const needed = Math.ceil(blockH + bandPad * 2 - (bandBottomPx - bandTopPx));
          const grow = Math.min(bandGrowCap, Math.max(0, needed));
          if (grow > 0) {
            const fill = measureBandFill(frameImg, frameUrl ?? '', 1350, Math.min(1349, bandTopPx + 3));
            if (fill) {
              ctx.save();
              // Обводка/тень уже настроены под текст — прямоугольнику они не нужны.
              ctx.shadowColor = 'transparent';
              ctx.shadowBlur = 0;
              ctx.fillStyle = fill.color;
              // +2 px внахлёст вниз: антиалиасный край PNG не должен просвечивать.
              ctx.fillRect(fill.x0, bandTopPx - grow, fill.x1 - fill.x0, grow + 2);
              ctx.restore();
              bandTopPx -= grow;
              if (bandTopLimit !== undefined) bandTopLimit -= grow;
            }
          }
        }
        const startY = bandTopPx !== undefined && bandBottomPx !== undefined
          ? centerBlockInBand({
              bandTop: bandTopPx,
              bandBottom: bandBottomPx,
              blockHeight: blockH,
              inkTop,
              inkHeight: Math.max(1, inkBottom - inkTop),
              canvasHeight: 1350,
            })
          : clampFrameTextY({
              anchoredY,
              blockHeight: blockH,
              canvasHeight: 1350,
              margin: FRAME_TEXT_MARGIN,
              topLimit: bandTopLimit,
              bottomLimit: bandBottomLimit,
            });
        if (textSoftShadowEnabled) {
          ctx.save();
          ctx.shadowColor = colorWithOpacity(textSoftShadowColor, textSoftShadowOpacity ?? 100);
          ctx.shadowBlur = textSoftShadowBlur * scale;
          ctx.shadowOffsetX = (textSoftShadowOffsetX ?? 2) * scale;
          ctx.shadowOffsetY = (textSoftShadowOffsetY ?? 4) * scale;
          // Большое размытие бледнит тень → несколько проходов аккумулируют альфу.
          const softPasses = Math.min(3, 1 + Math.round(textSoftShadowBlur / 14));
          for (let p = 0; p < softPasses; p++) lines.forEach((line, i) => {
            if (!line.trim()) return;
            paintLine(line, i, startY + i * lh);
          });
          ctx.restore();
        }
        lines.forEach((line, i) => {
          if (!line.trim()) return;
          if (textShadowEnabled) ctx.strokeText(line, fX, startY + i * lh);
          paintLine(line, i, startY + i * lh);
        });
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      }

      // Лого НЕ рисуем: в режиме frame оно уже впечатано дизайнером в PNG-каркас.

      return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (!blob) { reject(new Error('Failed to create blob from canvas')); return; }
          resolve(blob);
        }, 'image/png', 1.0);
      });
    }

    // 2. Геометрия плашки. Высота больше не фиксирована: считается из текста,
    // плашка прижата к низу и растёт вверх, потолок — в resolveBandLayout.
    // Лого нужно получить раньше отрисовки: от него зависит ширина переноса.
    const logoOverride = options.logoSize && options.logoSize > 0 ? options.logoSize : null;
    const logoSizeTop = logoOverride ?? 64 * scale;
    const logoSizeBottom = logoOverride ?? 48 * scale;
    const logoPadding = 16 * scale;
    const logoImg = await logoPromise;

    const bandGap = 12 * scale;
    const hasLogoInBottom = !!(logoImg && logoPosition && logoPosition.startsWith('bottom'));
    const bandTextMaxWidth =
      (1080 - 16 * scale * 2 - (hasLogoInBottom ? logoSizeBottom + bandGap : 0)) * 0.97;
    const bandText = textUppercase ? (text ?? '').toUpperCase() : (text ?? '');
    const band = bandText.trim()
      ? resolveBandLayout({
          text: bandText,
          fontSize: (fontSize && fontSize > 0 ? fontSize : DEFAULT_PREVIEW_FONT_SIZE) * scale,
          lineSpacing: options.textLineSpacing && options.textLineSpacing > 0 ? options.textLineSpacing : 1.2,
          maxWidth: bandTextMaxWidth,
          canvasHeight,
          fontFamily: actualFontFamily,
          ctx,
        })
      : null;

    // С градиентом фото идёт под плашку на всю высоту, без — заканчивается на ней.
    const textBlockHeight = band ? band.bandHeight : 270;
    const textBlockY = canvasHeight - textBlockHeight;
    const imageHeight = useGradient ? canvasHeight : textBlockY;

    const img = mediaImg;
    if (img) {
      drawMediaFitted(ctx, img, { x: 0, y: 0, w: 1080, h: imageHeight }, fitMode);
    }

    // 3. Рисуем логотип в верхней части (если позиция top-*)
    // Tailwind: h-16 w-16 = 64px для верхних позиций, top-4 left-4 = 16px
    // Размеры лого и сам logoImg получены выше: от них зависит ширина переноса,
    // а перенос — высота плашки, которую надо знать до отрисовки фото.
    if (logoImg && logoPosition && !logoPosition.startsWith('bottom')) {
      try {
        
        console.log('🖼️ Logo:', { logoSizeTop, logoPadding, logoPosition });
        
        let logoX, logoY;
        switch (logoPosition) {
          case 'top-left':
            logoX = logoPadding;
            logoY = logoPadding;
            break;
          case 'top-center':
            logoX = 540 - logoSizeTop / 2;
            logoY = logoPadding;
            break;
          case 'top-right':
            logoX = 1080 - logoSizeTop - logoPadding;
            logoY = logoPadding;
            break;
        }
        
        ctx.drawImage(logoImg, logoX, logoY, logoSizeTop, logoSizeTop);
      } catch (error) {
        console.warn('Failed to load logo:', error);
      }
    }

    // 4. Рисуем текстовый блок (полоса 270px внизу, координаты посчитаны выше)
    if (useGradient) {
      // Градиент СНИЗУ ВВЕРХ (как в превью: to top)
      // createLinearGradient(x0, y0, x1, y1) - от (x0,y0) к (x1,y1)
      // 0 = y0 (верх текстового блока), 1 = y1 (низ текстового блока)
      const gradient = ctx.createLinearGradient(0, textBlockY + textBlockHeight, 0, textBlockY);
      // Внизу (позиция 0) - backgroundColor с backgroundAlpha
      gradient.addColorStop(0, hexToRgba(backgroundColor, backgroundAlpha ?? 1));
      // Вверху (позиция 1) - backgroundColorEnd с backgroundAlphaEnd
      gradient.addColorStop(1, hexToRgba(backgroundColorEnd || backgroundColor, backgroundAlphaEnd ?? backgroundAlpha ?? 1));
      ctx.fillStyle = gradient;
    } else {
      ctx.fillStyle = hexToRgba(backgroundColor, backgroundAlpha ?? 1);
    }
    ctx.fillRect(0, textBlockY, 1080, textBlockHeight);

    // 5. Рисуем логотип в нижней части (если позиция bottom-*)
    if (logoImg && logoPosition && logoPosition.startsWith('bottom')) {
      try {
        
        const gapSize = 12 * scale; // gap-3 = 12px
        let logoX, logoY;
        
        switch (logoPosition) {
          case 'bottom-left':
            logoX = logoPadding;
            logoY = textBlockY + (textBlockHeight - logoSizeBottom) / 2;
            break;
          case 'bottom-right':
            logoX = 1080 - logoSizeBottom - logoPadding;
            logoY = textBlockY + (textBlockHeight - logoSizeBottom) / 2;
            break;
          case 'bottom-center':
            logoX = (1080 - logoSizeBottom) / 2;
            logoY = textBlockY + (textBlockHeight - logoSizeBottom) / 2;
            break;
        }

        ctx.drawImage(logoImg, logoX, logoY, logoSizeBottom, logoSizeBottom);
      } catch (error) {
        console.warn('Failed to load logo in text area:', error);
      }
    }

    // 6. Рисуем текст
    if (band) {
      // Перенос и кегль уже посчитаны в шаге 2 — плашка построена под них,
      // пересчитывать здесь нельзя, иначе высота полосы и текст разойдутся.
      const gapSize = bandGap;
      const maxWidth = bandTextMaxWidth;
      const { fontSize: optimalFontSize, lines } = band;
      
      if (textShadowEnabled) {
        ctx.strokeStyle = colorWithOpacity(textShadowColor, textShadowOpacity ?? 100);
        ctx.lineWidth = (textShadowWidth ?? 4) * 2 * scale;
        ctx.lineJoin = 'round';
        ctx.shadowColor = colorWithOpacity(textShadowColor, textShadowOpacity ?? 100);
        ctx.shadowBlur = textShadowBlur * scale;
      }
      // Применяем variable font настройки или fallback на fontBold
      applyFontVariationToCanvas(ctx, optimalFontSize, actualFontFamily, fontVariationSettings, fontBold);
      ctx.fillStyle = textColor;
      ctx.textBaseline = 'middle';

      // ВАЖНО: textX нужно сдвинуть если есть лого!
      let textX = 540; // По умолчанию центр

      if (logoPosition === 'bottom-left') {
        // Лого слева - сдвигаем текст вправо на половину (лого+gap)
        textX = 540 + (logoSizeBottom + gapSize) / 2;
      } else if (logoPosition === 'bottom-right') {
        // Лого справа - сдвигаем текст влево на половину (лого+gap)
        textX = 540 - (logoSizeBottom + gapSize) / 2;
      }
      const { x: textDrawX, a: textDrawAlign } = alignedX(textX, maxWidth);
      ctx.textAlign = textDrawAlign;

      console.log('📝 Text rendering:', { 
        optimalFontSize, 
        linesCount: lines.length, 
        maxWidth, 
        hasLogoInBottom, 
        textX, 
        logoPosition 
      });
      
      const textY = textBlockY + textBlockHeight / 2; // Центр по вертикали
      const lineHeight = optimalFontSize * (options.textLineSpacing && options.textLineSpacing > 0 ? options.textLineSpacing : 1.2);
      
      const totalHeight = lines.length * lineHeight;
      const startY = textY - totalHeight / 2 + lineHeight / 2;

      // Мягкая тень — отдельный pre-pass позади чёткого текста.
      if (textSoftShadowEnabled) {
        ctx.save();
        ctx.shadowColor = colorWithOpacity(textSoftShadowColor, textSoftShadowOpacity ?? 100);
        ctx.shadowBlur = textSoftShadowBlur * scale;
        ctx.shadowOffsetX = (textSoftShadowOffsetX ?? 2) * scale;
        ctx.shadowOffsetY = (textSoftShadowOffsetY ?? 4) * scale;
        // Большое размытие бледнит тень → несколько проходов аккумулируют альфу.
        const softPasses = Math.min(3, 1 + Math.round(textSoftShadowBlur / 14));
        for (let p = 0; p < softPasses; p++) lines.forEach((line, i) => {
          if (!line.trim()) return;
          ctx.fillStyle = useTwoColors && textColorSecondary && i !== 0 ? textColorSecondary : textColor;
          ctx.fillText(line, textDrawX, startY + i * lineHeight);
        });
        ctx.restore();
      }

      lines.forEach((line, i) => {
        const lineY = startY + i * lineHeight;
        // Если используются два цвета - первая строка одного цвета, остальные другого
        if (useTwoColors && textColorSecondary) {
          ctx.fillStyle = i === 0 ? textColor : textColorSecondary;
        }
        // Рисуем только непустые строки
        if (line.trim()) {
          if (textShadowEnabled) ctx.strokeText(line, textDrawX, lineY);
          ctx.fillText(line, textDrawX, lineY);
        }
      });
      // Сбрасываем тень
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
    }

    // 7. Рисуем декоративную линию
    if (showLine) {
      ctx.fillStyle = lineColor;
      // Масштабируем высоту линии
      const scaledLineHeight = lineHeight * scale;
      // Линия рисуется в текстовом блоке (в самом верху или низу блока)
      const lineY = linePosition === 'top' ? textBlockY : textBlockY + textBlockHeight - scaledLineHeight;
      ctx.fillRect(0, lineY, 1080, scaledLineHeight);
    }

    // 8. Конвертируем в blob и скачиваем
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to create blob from canvas'));
          return;
        }

        resolve(blob);
      }, 'image/png', 1.0);
    });
  } catch (error) {
    console.error('❌ Error exporting template to PNG:', error);
    throw error;
  }
}